import { Button, Intent, Spinner } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { FieldGroup, FieldSet, showError, useApiEffect } from "nsitools-react";
import * as React from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import styled from "styled-components";

import { PersonApi, PersonFusionDto } from "../../../api";
import { FGSRSHCheckboxInput, InformationCard, SmallFormGenerator } from "../../../components";
import { ERoutes } from "../../../ERoutes";
import { useApiService, useSaveApi, useTl } from "../../../hooks";
import { ETLCodes } from "../../../locales";
import { FusionDisplayRadio } from "../components/FusionDisplayRadio";
import { PersonFusion } from "./PersonFusion";

export interface IPersonFusionPageProps {}

const StyledSpace = styled.div`
  margin-bottom: 1rem;
`;

export const PersonFusionPage: React.FunctionComponent<IPersonFusionPageProps> = (props) => {
  const api = useApiService(PersonApi);
  const { id } = useParams<{ id: string }>();
  const { t } = useTl();
  const history = useHistory();
  const fusionId2 = window.localStorage.getItem("personFusionId");

  const [selectLeft, setselectLeft] = React.useState(null);

  /* ========== Getting Data =========== */
  const [data, loading] = useApiEffect(() => api.getPersonFusion(+id, +fusionId2));
  const [personComparison, ploading] = useApiEffect(() => api.getPersonFusionComparison(+id, +fusionId2));
  /* =================================== */

  const { saving, saveItem } = useSaveApi(
    React.useMemo(
      () => ({
        saveApiFn: async (d: PersonFusionDto) => {
          const result = await api.savePersonFusion(d);
          history.push(`${ERoutes.people}/${result}/detail/view`);
        },
      }),
      [api, history]
    )
  );

  const save = React.useCallback(
    (formikData) => {
      if (
        formikData.fusingPerson1.kennelId !== null &&
        formikData.fusingPerson2.kennelId !== null &&
        formikData.fusingPerson1.kennelId !== formikData.fusingPerson2.kennelId
      ) {
        showError(t(ETLCodes.PersonFusionKennelErrorMessage));
      } else {
        saveItem(formikData.fusion);
      }
    },
    [saveItem, t]
  );

  /* ===================================*/

  return loading || ploading ? (
    <Spinner />
  ) : (
    <SmallFormGenerator
      initialValues={{
        fusion: data,
        fusingPerson1: personComparison.fusingPerson1,
        fusingPerson2: personComparison.fusingPerson2,
        personComparison: personComparison,
      }}
      dirtyCheckSaveButton={false}
      onSubmit={save}
      editMode={true}
      loading={loading || ploading}
      saving={saving}
      enableDirtyCheck={false}
    >
      <FieldGroup columns={2}>
        <FieldSet
          title={
            <Link target="_blank" to={`${ERoutes.people}/${personComparison.fusingPerson1.personId}/detail/view`}>
              {personComparison.fusingPerson1.computedName}
            </Link>
          }
          rightElement={
            <Button
              text={t(ETLCodes.SelectOther)}
              intent={Intent.PRIMARY}
              minimal={false}
              icon={IconNames.SWAP_HORIZONTAL}
              onClick={() => {
                if (selectLeft === null) {
                  setselectLeft(false);
                } else {
                  setselectLeft(!selectLeft);
                }
              }}
            />
          }
        />
        <FieldSet
          title={
            <Link target="_blank" to={`${ERoutes.people}/${personComparison.fusingPerson1.personId}/detail/view`}>
              {personComparison.fusingPerson2.computedName}
            </Link>
          }
        />
      </FieldGroup>
      <PersonFusion
        person1={personComparison.fusingPerson1}
        person2={personComparison.fusingPerson2}
        selectLeft={selectLeft}
      />

      <StyledSpace />
      <FieldSet title={t(ETLCodes.Dogs)}>
        <FieldGroup columns={2}>
          <FGSRSHCheckboxInput name="personComparison.fusingPerson1HasDogs" label={t(ETLCodes.HasDogs)} readonly />
          <FGSRSHCheckboxInput name="personComparison.fusingPerson2HasDogs" label={t(ETLCodes.HasDogs)} readonly />
        </FieldGroup>
        <InformationCard text={t(ETLCodes.PersonFusionDogsMessage)} />
      </FieldSet>

      <FieldSet title={t(ETLCodes.WebAccess)}>
        <FusionDisplayRadio
          label={ETLCodes.PortalAccount}
          fusionName="fusion.fusion.potalPersonId"
          fusionId1={+id}
          fusionId2={+fusionId2}
          disabled={data.portalDisabled}
        />
        {!data.portalDisabled && <InformationCard text={t(ETLCodes.PersonFusionPortalMessage)} type="Information" />}
      </FieldSet>
      <StyledSpace />

      <FieldSet title={t(ETLCodes.Requests)}>
        <FieldGroup columns={2}>
          <FGSRSHCheckboxInput
            name="personComparison.fusingPerson1HasRequests"
            label={t(ETLCodes.HasRequests)}
            readonly
          />
          <FGSRSHCheckboxInput
            name="personComparison.fusingPerson2HasRequests"
            label={t(ETLCodes.HasRequests)}
            readonly
          />
        </FieldGroup>
        <InformationCard text={t(ETLCodes.PersonFusionRequestsMessage)} />
      </FieldSet>
      <StyledSpace />

      <FieldSet title={t(ETLCodes.Breeders)}>
        <FusionDisplayRadio
          label={ETLCodes.IsBreeder}
          fusionName="fusion.fusion.breederPersonId"
          fusionId1={+id}
          fusionId2={+fusionId2}
          disabled={data.breederDisabled}
        />
        <FieldGroup columns={2}>
          <div style={{ marginLeft: "4em" }}>
            {personComparison.fusingPerson1.hkNumber && `${personComparison.fusingPerson1.hkNumber}`}
          </div>
          <div style={{ marginLeft: "4em" }}>
            {personComparison.fusingPerson2.hkNumber && `${personComparison.fusingPerson2.hkNumber}`}
          </div>
        </FieldGroup>
      </FieldSet>
      <StyledSpace />

      <FieldSet title={t(ETLCodes.Member)}>
        <FusionDisplayRadio
          label={ETLCodes.IsAMember}
          fusionName="fusion.fusion.membershipPersonId"
          fusionId1={+id}
          fusionId2={+fusionId2}
          disabled={data.membershipDisabled}
        />
        {!data.membershipDisabled && (
          <InformationCard text={t(ETLCodes.PersonFusionMemberMessage)} type="Information" />
        )}
      </FieldSet>
      <StyledSpace />

      <FieldSet title={t(ETLCodes.Breeder)}>
        <FieldSet title={t(ETLCodes.Kennels)}>
          <FieldGroup columns={2}>
            <FGSRSHCheckboxInput
              name="personComparison.fusingPerson1HasKennel"
              label={t(ETLCodes.HasKennel)}
              readonly
            />
            <FGSRSHCheckboxInput
              name="personComparison.fusingPerson2HasKennel"
              label={t(ETLCodes.HasKennel)}
              readonly
            />

            {personComparison.fusingPerson1.kennelId !== null && (
              <div style={{ marginLeft: "4em" }}>
                <Link target="_blank" to={`${ERoutes.kennel}/${personComparison.fusingPerson1.kennelId}/detail/view`}>
                  {`${personComparison.fusingPerson1.kennelName}`}
                </Link>
              </div>
            )}
            {personComparison.fusingPerson2.kennelId !== null && (
              <div style={{ marginLeft: "4em" }}>
                <Link target="_blank" to={`${ERoutes.kennel}/${personComparison.fusingPerson2.kennelId}/detail/view`}>
                  {`${personComparison.fusingPerson2.kennelName}`}
                </Link>
              </div>
            )}
            {personComparison.fusingPerson1.kennelId !== personComparison.fusingPerson2.kennelId &&
              personComparison.fusingPerson1.kennelId !== null &&
              personComparison?.fusingPerson2.kennelId !== null && (
                <InformationCard text={t(ETLCodes.PersonFusionKennelErrorMessage)} type="Error" />
              )}
          </FieldGroup>
        </FieldSet>
        <StyledSpace />

        <FieldSet title={t(ETLCodes.Dogs)}>
          <InformationCard text={t(ETLCodes.PersonFusionBreederDogsMessage)} type="Information" />
        </FieldSet>
      </FieldSet>
      <StyledSpace />

      <FieldSet title={t(ETLCodes.Judge)}>
        <FusionDisplayRadio
          label={ETLCodes.IsJudge}
          fusionName="fusion.judgePersonId"
          fusionId1={+id}
          fusionId2={+fusionId2}
          disabled={data.judgeDisabled}
        />
        {!data.judgeDisabled && <InformationCard text={t(ETLCodes.PersonFusionJudgeMessage)} type="Information" />}
      </FieldSet>
      <StyledSpace />

      <FieldSet title={t(ETLCodes.Commissioner)}>
        <FusionDisplayRadio
          label={ETLCodes.IsCommissioner}
          fusionName="fusion.commissionerPersonId"
          fusionId1={+id}
          fusionId2={+fusionId2}
          disabled={data.commissionerDisabled}
        />
        {!data.commissionerDisabled && (
          <InformationCard text={t(ETLCodes.PersonFusionCommissionerMessage)} type="Information" />
        )}
      </FieldSet>
      <StyledSpace />

      <FieldSet title={t(ETLCodes.Veterinarian)}>
        <FusionDisplayRadio
          label={ETLCodes.IsVet}
          fusionName="fusion.fusion.vetPersonId"
          fusionId1={+id}
          fusionId2={+fusionId2}
          disabled={data.vetDisabled}
        />
        {!data.vetDisabled && <InformationCard text={t(ETLCodes.PersonFusionVetMessage)} type="Information" />}
      </FieldSet>
    </SmallFormGenerator>
  );
};
